import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Instagram, Facebook, Linkedin, Youtube } from 'lucide-react';
import { Button } from '../components/ui/button';

/* TikTok icon (not in lucide-react) */
const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.87a8.16 8.16 0 0 0 4.76 1.52v-3.4a4.85 4.85 0 0 1-1-.3z" />
  </svg>
);

/* WhatsApp icon */
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
  </svg>
);

const socialLinks = [
  { name: 'Instagram', icon: Instagram, href: 'https://www.instagram.com/servitiumpro/' },
  { name: 'Facebook', icon: Facebook, href: 'https://www.facebook.com/profile.php?id=61578237311186' },
  { name: 'LinkedIn', icon: Linkedin, href: 'https://www.linkedin.com/company/101705843/admin/dashboard/' },
  { name: 'WhatsApp', icon: WhatsAppIcon, href: 'https://wa.me/message/RAX52YKJZCL4A1', isCustom: true },
  { name: 'YouTube', icon: Youtube, href: 'https://www.youtube.com/@SERVITIUMPRO' },
  { name: 'TikTok', icon: TikTokIcon, href: 'https://www.tiktok.com/@servitium_pro?_r=1&_t=ZS-93oVmKn2IS4', isCustom: true },
];

const ContactUsPage = () => (
  <main className="pt-16 md:pt-20 bg-[#FAFAF9]" data-testid="contact-page" style={{ fontFamily: "'Open Sans', sans-serif" }}>
    <Helmet>
      <title>Contact — Servitium</title>
      <meta name="description" content="Get in touch with Servitium. For business inquiries, support, partnerships, or press — reach us at support@servitium.ca." />
      <meta property="og:title" content="Contact — Servitium" />
      <meta property="og:description" content="Get in touch with Servitium. For business inquiries, support, partnerships, or press — reach us at support@servitium.ca." />
      <meta property="og:url" content="https://servitium.ca/contact-us" />
      <meta name="twitter:title" content="Contact — Servitium" />
      <meta name="twitter:description" content="Get in touch with Servitium. For business inquiries, support, partnerships, or press — reach us at support@servitium.ca." />
      <meta property="og:image" content="https://servitium.ca/og-preview.png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Servitium — Infrastructure for solo and micro-businesses" />
      <meta name="twitter:image" content="https://servitium.ca/og-preview.png" />
    </Helmet>

    {/* ══ SECTION 1: CONTACT INFO ══ */}
    <section className="py-20 md:py-28" data-testid="contact-info-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#880808] mb-4">
            CONTACT
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#36454F] tracking-tight leading-tight mb-6" data-testid="contact-headline">
            Get in touch.
          </h1>
          <p className="text-base md:text-lg text-[#57534E] leading-relaxed max-w-2xl mx-auto">
            For business inquiries, support, partnerships, or press — reach us at the address below. We read everything that comes in.
          </p>
        </div>

        {/* 3-column contact details */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          {/* EMAIL */}
          <div className="text-center" data-testid="contact-email">
            <p className="text-[10px] font-extrabold tracking-[0.18em] uppercase text-[#880808] mb-3">EMAIL</p>
            <a
              href="mailto:support@servitium.ca"
              className="text-base md:text-lg font-medium text-[#880808] hover:text-[#6b0606] underline underline-offset-4 transition-colors"
              data-testid="contact-email-link"
            >
              support@servitium.ca
            </a>
          </div>

          {/* LOCATION */}
          <div className="text-center" data-testid="contact-location">
            <p className="text-[10px] font-extrabold tracking-[0.18em] uppercase text-[#880808] mb-3">LOCATION</p>
            <p className="text-base md:text-lg text-[#36454F]">
              Metro Vancouver,<br />British Columbia, Canada
            </p>
          </div>

          {/* SOCIAL */}
          <div className="text-center" data-testid="contact-social">
            <p className="text-[10px] font-extrabold tracking-[0.18em] uppercase text-[#880808] mb-3">SOCIAL</p>
            <div className="flex items-center justify-center gap-3">
              {socialLinks.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg border border-[#E7E5E4] bg-white flex items-center justify-center text-[#57534E] transition-all duration-200 hover:border-[#880808] hover:text-[#880808] hover:shadow-sm"
                  aria-label={s.name}
                  data-testid={`contact-social-${s.name.toLowerCase()}`}
                >
                  {s.isCustom ? <s.icon /> : <s.icon className="w-5 h-5" strokeWidth={1.5} />}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ══ SECTION 2: FINAL CTA ══ */}
    <section className="py-20 md:py-28 bg-white" data-testid="contact-cta-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-[#36454F] tracking-tight leading-tight mb-8">
          Ready to start?
        </h2>
        <div className="flex flex-col items-center gap-4 mb-4">
          <Link to="/get" data-testid="contact-cta-btn">
            <Button className="bg-[#880808] hover:bg-[#6b0606] text-white rounded-full px-10 py-5 text-lg font-bold shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105">
              Join free
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <a href="#" aria-label="Download on the App Store">
              <img src="/apple-badge.svg" alt="App Store" className="h-10 opacity-80 hover:opacity-100 transition-opacity" onError={(e) => { e.target.style.display = 'none'; }} />
            </a>
            <a href="#" aria-label="Get it on Google Play">
              <img src="/google-badge.svg" alt="Google Play" className="h-10 opacity-80 hover:opacity-100 transition-opacity" onError={(e) => { e.target.style.display = 'none'; }} />
            </a>
          </div>
        </div>
        <p className="text-sm text-[#6b7280]">
          Free across the Lower Mainland · $25/month elsewhere
        </p>
      </div>
    </section>

    <div className="pb-8" />
  </main>
);

export default ContactUsPage;
